import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../styles/atoms.scss"; // Ensure styles are applied
import DropDownz from "../atoms/DropDownz";
import Inputz from "../atoms/Inputz";
import TextAreaz from "../atoms/TextAreaz";
import Buttonz from "../atoms/Buttonz";
import "./ContactC.scss";
import pb from "../../services/pocketbase";
import { useDispatch } from "react-redux";
import { validationPopup } from "../../redux/actions";

const ContactZ = () => {
  const dispatch = useDispatch();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [mode, setMode] = useState("form"); // "form" | "thanks"
  const resetTimerRef = useRef(null);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const errors = useMemo(() => {
    const next = {};
    const name = String(form.name || "").trim();
    const email = String(form.email || "").trim();
    const subject = String(form.subject || "").trim();
    const message = String(form.message || "").trim();

    if (!name) next.name = "Please enter your name.";

    if (!email) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      next.email = "Please enter a valid email address.";

    if (!subject) next.subject = "Please choose a subject.";

    if (!message) next.message = "Please enter a message.";
    else if (message.length < 10)
      next.message = "Message is too short (min 10 characters).";

    return next;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  const showThanksThenReset = () => {
    setMode("thanks");
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setMode("form");
      setSubmitting(false);
      setSubmitError("");
      setTouched({});
      setForm({
        name: "",
        email: "",
        company: "",
        subject: "",
        message: "",
      });
    }, 7000);
  };

  const onAnyChange = (target) => {
    // Inputz/TextAreaz pass e.target; DropDownz passes {name,value}
    const name = target?.name;
    const value = target?.value ?? "";
    if (!name) return;
    setField(name, value);
  };

  const onBlur = (e) => {
    const name = e?.target?.name;
    if (!name) return;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const markAllTouched = () => {
    setTouched({
      name: true,
      email: true,
      company: true,
      subject: true,
      message: true,
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    markAllTouched();
    if (!isValid || submitting) {
      dispatch(
        validationPopup({
          message: "Please fix the highlighted fields.",
          type: "error",
          icon: "info",
        })
      );
      return;
    }

    setSubmitting(true);
    try {
      await pb.collection("contact_messages").create({
        name: String(form.name || "").trim(),
        email: String(form.email || "").trim(),
        company: String(form.company || "").trim(),
        subject: String(form.subject || "").trim(),
        message: String(form.message || "").trim(),
      });
      dispatch(
        validationPopup({
          message: "Thanks! Your message has been sent.",
          type: "success",
          icon: "check",
        })
      );
      showThanksThenReset();
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.message ||
        "Sorry — something went wrong. Please try again.";
      setSubmitError(String(msg));
      dispatch(
        validationPopup({
          message: "Send failed. Please try again in a moment.",
          type: "error",
          icon: "warning",
        })
      );
      setSubmitting(false);
    }
  };

  return (

<div className="contact-hero">
    <div className="contact-hero-content">
        <h2 className="title">CONTACT US</h2>
        <p className="subtitle">
          Welcome to our UUD! <br />
          We're just starting out and value your input.<br />
          Whether you have feedback, suggestions, or want to partner with us, we're all ears. Feel free to reach out - your contribution matters!
          </p>
    </div>

    {mode === "thanks" ? (
      <div className="contact-thanks" role="status" aria-live="polite">
        <div className="contact-thanks-title">Thanks!</div>
        <div className="contact-thanks-subtitle">
          We received your message and will get back to you soon.
        </div>
      </div>
    ) : (
      <form className="contact-formz" onSubmit={handleSubmit} noValidate>
        <div className="field" data-has-error={!!(touched.name && errors.name)}>
          <Inputz
            iconL="user"
            name="name"
            placeholder="Name*"
            className="input"
            value={form.name}
            onChange={onAnyChange}
            onBlur={onBlur}
            error={!!(touched.name && errors.name)}
            disable={submitting}
          />
          {touched.name && errors.name && (
            <div className="tooltip-error" role="tooltip">
              {errors.name}
            </div>
          )}
        </div>

        <div className="field" data-has-error={!!(touched.email && errors.email)}>
          <Inputz
            iconL="mail"
            name="email"
            placeholder="Your Email*"
            className="input"
            value={form.email}
            onChange={onAnyChange}
            onBlur={onBlur}
            error={!!(touched.email && errors.email)}
            disable={submitting}
          />
          {touched.email && errors.email && (
            <div className="tooltip-error" role="tooltip">
              {errors.email}
            </div>
          )}
        </div>

        <div className="field">
          <Inputz
            iconL="company"
            name="company"
            placeholder="Company"
            className="input"
            value={form.company}
            onChange={onAnyChange}
            onBlur={onBlur}
            disable={submitting}
          />
        </div>

        <div
          className="field"
          data-has-error={!!(touched.subject && errors.subject)}
        >
          <DropDownz
            iconL="hum"
            iconR="drop_down"
            iconRHover="drop_up"
            iconClear="clear"
            hoverIconR="mail"
            placeholder="Subject*"
            className="dropdown_input"
            name="subject"
            value={form.subject}
            error={!!(touched.subject && errors.subject)}
            onSelect={(payload) => {
              onAnyChange(payload);
              setTouched((prev) => ({ ...prev, subject: true }));
            }}
          >
            <span>Feedback/Issues Reporting</span>
            <span>Request/Suggestion</span>
            <span>Partnership Offer</span>
            <span>Sponsoring</span>
            <span>Say Hello!</span>
          </DropDownz>
          {touched.subject && errors.subject && (
            <div className="tooltip-error" role="tooltip">
              {errors.subject}
            </div>
          )}
        </div>

        <div
          className="field"
          data-has-error={!!(touched.message && errors.message)}
        >
          <TextAreaz
            title="Your Message*"
            iconL="chat"
            maxLength={500}
            className="custom-input insider"
            name="message"
            value={form.message}
            onChange={onAnyChange}
            error={!!(touched.message && errors.message)}
            onBlur={onBlur}
            disable={submitting}
          />
          {touched.message && errors.message && (
            <div className="tooltip-error" role="tooltip">
              {errors.message}
            </div>
          )}
        </div>

        {submitError && <div className="contact-submit-error">{submitError}</div>}

        <Buttonz
          iconR="mail"
          label={submitting ? "Sending..." : "Send"}
          onClick={handleSubmit}
          className={submitting ? "disabled" : ""}
        />
      </form>
    )}
         
      
    
      </div>
  );
};

export default ContactZ;
